// Text Block Component
// Rich text editor block with modal editing
import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { Type } from 'lucide-react';

const TextBlock = ({ content, padding, backgroundColor, onUpdate }) => {
  const [showEditModal, setShowEditModal] = useState(false);
  const [text, setText] = useState(content?.html || '');

  // Check if this is a new/empty block
  const isEmptyBlock = !content?.html;
  const [fontFamily, setFontFamily] = useState(content?.fontFamily || 'system');
  const [bgColor, setBgColor] = useState(backgroundColor || '');
  const [editPadding, setEditPadding] = useState(padding || 'medium');

  // Font options
  const fonts = [
    { value: 'system', label: 'System Default' },
    { value: 'Coustard', label: 'Coustard' },
    { value: 'Alfa Slab One', label: 'Alfa Slab One' },
    { value: 'Ovo', label: 'Ovo' },
    { value: 'Fraunces', label: 'Fraunces' },
    { value: 'Lancelot', label: 'Lancelot' },
    { value: 'Arvo', label: 'Arvo' },
    { value: 'Josefin Slab', label: 'Josefin Slab' },
    { value: 'Outfit', label: 'Outfit' },
    { value: 'Cal Sans', label: 'Cal Sans' },
    { value: 'Tilt Warp', label: 'Tilt Warp' },
    { value: 'McLaren', label: 'McLaren' },
    { value: 'Mate', label: 'Mate' },
    { value: 'Buda', label: 'Buda' },
    { value: 'Bellefair', label: 'Bellefair' },
    { value: 'Questrial', label: 'Questrial' },
    { value: 'DM Mono', label: 'DM Mono' },
    { value: 'Quicksand', label: 'Quicksand' },
    { value: 'Nunito Sans', label: 'Nunito Sans' },
    { value: 'Roboto', label: 'Roboto' },
    { value: 'Manrope', label: 'Manrope' },
    { value: 'Jost', label: 'Jost' }
  ];

  const getFontFamily = (fontName) => {
    if (!fontName || fontName === 'system') {
      return 'ui-sans-serif, system-ui, sans-serif';
    }
    return `"${fontName}", sans-serif`;
  };

  const handleSave = () => {
    onUpdate({
      content: {
        html: text,
        fontFamily
      },
      padding: editPadding,
      backgroundColor: bgColor || null
    });
    setShowEditModal(false);
  };

  const handleCancel = () => {
    setText(content?.html || '');
    setFontFamily(content?.fontFamily || 'system');
    setBgColor(backgroundColor || '');
    setEditPadding(padding || 'medium');
    setShowEditModal(false);
  };

  const openEditModal = () => {
    setText(content?.html || '');
    setFontFamily(content?.fontFamily || 'system');
    setBgColor(backgroundColor || '');
    setEditPadding(padding || 'medium');
    setShowEditModal(true);
  };

  const getPaddingClass = () => {
    switch (padding) {
      case 'none': return 'p-0';
      case 'small': return 'p-4';
      case 'large': return 'p-12';
      default: return 'p-8';
    }
  };

  const getAlignmentClass = () => {
    switch (content?.alignment) {
      case 'center': return 'text-center';
      case 'right': return 'text-right';
      default: return 'text-left';
    }
  };

  const modules = {
    toolbar: [
      [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
      [{ 'size': ['8px', '9px', '10px', '11px', '12px', '14px', '16px', '18px', '20px', '24px', '30px', '36px', '48px', '72px'] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'align': [] }],
      ['link'],
      ['clean']
    ],
  };

  // Expose openEditModal for PageBuilder
  TextBlock.openEditModal = openEditModal;

  const renderEditModal = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-[#3C3C3C] dark:text-gray-100">
            Edit Text Block
          </h3>
        </div>

        <div className="p-6 space-y-4 overflow-y-auto flex-1">
          {/* Font Selection */}
          <div>
            <label className="block text-sm font-medium text-[#3C3C3C] dark:text-gray-100 mb-2">
              Font Family
            </label>
            <select
              value={fontFamily}
              onChange={(e) => setFontFamily(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-[#3C3C3C] dark:text-gray-100"
              style={{ fontFamily: getFontFamily(fontFamily) }}
            >
              {fonts.map(font => (
                <option key={font.value} value={font.value} style={{ fontFamily: getFontFamily(font.value) }}>
                  {font.label}
                </option>
              ))}
            </select>
          </div>

          {/* Background Color */}
          <div>
            <label className="block text-sm font-medium text-[#3C3C3C] dark:text-gray-100 mb-2">
              Background Color
            </label>
            <div className="flex gap-2">
              <input
                type="color"
                value={bgColor || '#ffffff'}
                onChange={(e) => setBgColor(e.target.value)}
                className="h-10 w-20 rounded border border-gray-300 dark:border-gray-600 cursor-pointer"
              />
              <input
                type="text"
                value={bgColor || ''}
                onChange={(e) => setBgColor(e.target.value)}
                placeholder="#ffffff"
                className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-[#3C3C3C] dark:text-gray-100"
              />
              <button
                onClick={() => setBgColor('')}
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-[#3C3C3C] dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Clear
              </button>
            </div>
          </div>

          {/* Padding */}
          <div>
            <label className="block text-sm font-medium text-[#3C3C3C] dark:text-gray-100 mb-2">
              Padding
            </label>
            <select
              value={editPadding}
              onChange={(e) => setEditPadding(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-[#3C3C3C] dark:text-gray-100"
            >
              <option value="none">None</option>
              <option value="small">Small</option>
              <option value="medium">Medium</option>
              <option value="large">Large</option>
            </select>
          </div>

          {/* Rich Text Editor with live styling */}
          <div>
            <label className="block text-sm font-medium text-[#3C3C3C] dark:text-gray-100 mb-2">
              Content
            </label>
            <style>
              {`
                .edit-text-editor .ql-editor {
                  font-family: ${fontFamily && fontFamily !== 'system'
                    ? `"${fontFamily}", sans-serif`
                    : 'ui-sans-serif, system-ui, sans-serif'};
                  background-color: ${bgColor || '#ffffff'};
                  min-height: 150px;
                }
                .edit-text-editor .ql-snow .ql-picker.ql-size .ql-picker-label::before,
                .edit-text-editor .ql-snow .ql-picker.ql-size .ql-picker-item::before {
                  content: attr(data-value) !important;
                }
                .edit-text-editor .ql-snow .ql-picker.ql-size .ql-picker-label[data-value=""]::before,
                .edit-text-editor .ql-snow .ql-picker.ql-size .ql-picker-item[data-value=""]::before {
                  content: 'Normal' !important;
                }
              `}
            </style>
            <ReactQuill
              theme="snow"
              value={text}
              onChange={setText}
              modules={modules}
              className="bg-white dark:bg-gray-700 rounded edit-text-editor"
            />
          </div>
        </div>

        <div className="sticky bottom-0 bg-white dark:bg-gray-800 p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-2">
          <button
            onClick={handleCancel}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-[#3C3C3C] dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-2 bg-[#7A99D8] text-white rounded-lg hover:bg-[#6A89C8]"
          >
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );

  // Empty state - show placeholder
  if (isEmptyBlock) {
    return (
      <>
        {/* Hidden edit trigger for PageBuilder */}
        <button
          data-edit-trigger
          onClick={openEditModal}
          className="hidden"
          aria-hidden="true"
        />
        <div
          className={`${getPaddingClass()} h-full flex items-center justify-center`}
          style={{ backgroundColor }}
        >
          <div className="text-center">
            <Type className="w-12 h-12 text-gray-300 mx-auto mb-2" />
            <p className="text-sm text-gray-400">Use edit button to add text</p>
          </div>
        </div>

        {showEditModal && createPortal(renderEditModal(), document.body)}
      </>
    );
  }

  // Display text content
  return (
    <>
      {/* Hidden edit trigger for PageBuilder */}
      <button
        data-edit-trigger
        onClick={openEditModal}
        className="hidden"
        aria-hidden="true"
      />
      <div
        className={`${getPaddingClass()} ${getAlignmentClass()}`}
        style={{
          backgroundColor,
          color: content?.textColor || undefined,
          fontFamily: getFontFamily(content?.fontFamily)
        }}
      >
        <div
          className="prose prose-sm dark:prose-invert max-w-none"
          style={{ color: content?.textColor || 'inherit' }}
          dangerouslySetInnerHTML={{ __html: content?.html }}
        />
      </div>

      {showEditModal && createPortal(renderEditModal(), document.body)}
    </>
  );
};

// Make openEditModal accessible from outside
TextBlock.setShowEditModal = null;

export default TextBlock;
